// lib
import type { RouterConfig } from '@nuxt/schema'

// app
import { NEWS_SUBMODULE_CONFIG } from '~/features/news/submodule'
import { SUPPORTERS_SUBMODULE_CONFIG } from '~/features/supporters/submodule'
import { SURVEYS_SUBMODULE_CONFIG } from '~/features/surveys/submodule'
import { permissions } from '~/shared/lib'
import createMailRoutes from './routes/mail'
import createNewsRoutes from './routes/news'
import createSupportersRoutes from './routes/supporters'
import createSurveyRoutes from './routes/survey'

export default <RouterConfig>{
  routes: _routes => [
    {
      name: permissions.INDEX.PERMISSION,
      path: permissions.INDEX.ROUTE,
      component: () => import('~/pages/index/ui/IndexPage.vue'),
      meta: { layout: 'default' },
      redirect: { name: permissions.AUTH.PERMISSION },
    },
    {
      name: permissions.AUTH.PERMISSION,
      path: permissions.AUTH.ROUTE,
      component: () => import('~/pages/auth/home/ui/HomePage.vue'),
      meta: { layout: 'auth' },
    },
    {
      name: permissions.AUTH.SIGNUP.PERMISSION,
      path: permissions.AUTH.SIGNUP.ROUTE,
      component: () => import('~/pages/auth/signup/ui/SignupPage.vue'),
      meta: { layout: 'auth' },
    },
    {
      name: permissions.AUTH.VERIFY.PERMISSION,
      path: permissions.AUTH.VERIFY.ROUTE,
      component: () => import('~/pages/auth/verify/ui/VerifyPage.vue'),
      meta: { layout: 'auth' },
    },
    {
      name: permissions.AUTH.PROFILE.PERMISSION,
      path: permissions.AUTH.PROFILE.ROUTE,
      component: () => import('~/pages/auth/profile/ui/ProfilePage.vue'),
      meta: { layout: 'auth' },
    },
    {
      name: permissions.AUTH.SET_PASSWORD.PERMISSION,
      path: permissions.AUTH.SET_PASSWORD.ROUTE,
      component: () =>
        import('~/pages/auth/set-password/ui/SetPasswordPage.vue'),
      meta: { layout: 'auth' },
    },
    {
      name: permissions.AUTH.SIGNIN.PERMISSION,
      path: permissions.AUTH.SIGNIN.ROUTE,
      component: () => import('~/pages/auth/signin/ui/SigninPage.vue'),
      meta: { layout: 'auth' },
    },
    {
      name: permissions.AUTH.PIN.PERMISSION,
      path: permissions.AUTH.PIN.ROUTE,
      component: () => import('~/pages/auth/pin-unlock/ui/PinUnlockPage.vue'),
      meta: { layout: 'auth' },
    },
    {
      name: permissions.AUTH.PIN.SETUP.PERMISSION,
      path: permissions.AUTH.PIN.SETUP.ROUTE,
      component: () => import('~/pages/auth/pin-setup/ui/PinSetupPage.vue'),
      meta: { layout: 'auth' },
    },
    {
      name: permissions.AUTH.FORGOT_PASSWORD.PERMISSION,
      path: permissions.AUTH.FORGOT_PASSWORD.ROUTE,
      component: () =>
        import('~/pages/auth/forgot-password/ui/ForgotPasswordPage.vue'),
      meta: { layout: 'auth' },
    },
    {
      name: permissions.AUTH.FORGOT_PASSWORD.VERIFY.PERMISSION,
      path: permissions.AUTH.FORGOT_PASSWORD.VERIFY.ROUTE,
      component: () => import('~/pages/auth/verify/ui/VerifyPage.vue'),
      meta: { layout: 'auth' },
      props: { isReset: true },
    },
    {
      name: permissions.AUTH.FORGOT_PASSWORD.RESET.PERMISSION,
      path: permissions.AUTH.FORGOT_PASSWORD.RESET.ROUTE,
      component: () =>
        import('~/pages/auth/set-password/ui/SetPasswordPage.vue'),
      meta: { layout: 'auth' },
      props: { isReset: true },
    },
    {
      name: permissions.AUTH.FORGOT_PASSWORD.SUCCESS.PERMISSION,
      path: permissions.AUTH.FORGOT_PASSWORD.SUCCESS.ROUTE,
      component: () => import('~/pages/auth/success/ui/SuccessPage.vue'),
      meta: { layout: 'auth' },
    },
    {
      name: permissions.WORKSPACE.PERMISSION,
      path: permissions.WORKSPACE.ROUTE,
      component: () => import('~/pages/workspace/ui/WorkspacePage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.WORKSPACE.MANAGEMENT.MODULE.DETAIL.PERMISSION,
      path: permissions.WORKSPACE.MANAGEMENT.MODULE.DETAIL.ROUTE,
      component: () => import('~/pages/workspace/ui/WorkspaceManagementModuleDetailPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.LK.PERMISSION,
      path: permissions.LK.ROUTE,
      component: () => import('~/pages/profile/ui/ProfilePage.vue'),
      redirect: { name: permissions.PROJECTS.ALL.PERMISSION },
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.LK.PROFILE.PERMISSION,
      path: permissions.LK.PROFILE.ROUTE,
      component: () => import('~/pages/profile/ui/ProfilePage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.LK.SECURITY.PERMISSION,
      path: permissions.LK.SECURITY.ROUTE,
      component: () => import('~/pages/security/ui/SecurityPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.LK.NOTIFICATIONS.PERMISSION,
      path: permissions.LK.NOTIFICATIONS.ROUTE,
      component: () =>
        import('~/pages/notification-settings/ui/NotificationSettingsPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.LK.ADDRESS.PERMISSION,
      path: permissions.LK.ADDRESS.ROUTE,
      component: () => import('~/pages/address/ui/AddressPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.LK.ADDRESS.EDIT.PERMISSION,
      path: permissions.LK.ADDRESS.EDIT.ROUTE,
      component: () => import('~/pages/address/ui/AddressPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.COUNTERS.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.COUNTERS.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectsWorkspaceCountersPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.COUNTERS.DETAIL.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.COUNTERS.DETAIL.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectsWorkspaceCountersDetailPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.COUNTERS.HISTORY.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.COUNTERS.HISTORY.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectsWorkspaceCountersHistoryPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.WORKSPACE.CALENDAR.PERMISSION,
      path: permissions.WORKSPACE.CALENDAR.ROUTE,
      component: () => import('~/pages/calendar/ui/CalendarPage.vue'),
      meta: { layout: 'base-full', middleware: ['auth'] },
    },
    {
      name: permissions.WORKSPACE.CALENDAR.TASK_PARTICIPANTS.PERMISSION,
      path: permissions.WORKSPACE.CALENDAR.TASK_PARTICIPANTS.ROUTE,
      component: () => import('~/pages/calendar/ui/CalendarTaskParticipantsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.WORKSPACE.CALENDAR.TASK_COMMENTS.PERMISSION,
      path: permissions.WORKSPACE.CALENDAR.TASK_COMMENTS.ROUTE,
      component: () => import('~/pages/calendar/ui/CalendarTaskCommentsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.WORKSPACE.CALENDAR.TASK_INVITATIONS.PERMISSION,
      path: permissions.WORKSPACE.CALENDAR.TASK_INVITATIONS.ROUTE,
      component: () => import('~/pages/calendar/ui/CalendarTaskInvitationsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.CALENDAR.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.CALENDAR.ROUTE,
      component: () => import('~/pages/calendar/ui/CalendarPage.vue'),
      meta: { layout: 'base-full', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.CALENDAR.TASK_PARTICIPANTS.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.CALENDAR.TASK_PARTICIPANTS.ROUTE,
      component: () => import('~/pages/calendar/ui/CalendarTaskParticipantsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.CALENDAR.TASK_COMMENTS.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.CALENDAR.TASK_COMMENTS.ROUTE,
      component: () => import('~/pages/calendar/ui/CalendarTaskCommentsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.CALENDAR.TASK_INVITATIONS.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.CALENDAR.TASK_INVITATIONS.ROUTE,
      component: () => import('~/pages/calendar/ui/CalendarTaskInvitationsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.FRIENDS.PERMISSION,
      path: permissions.FRIENDS.ROUTE,
      component: () => import('~/pages/friends/ui/FriendsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.USER.PERMISSION,
      path: permissions.USER.ROUTE,
      component: () => import('~/pages/user/ui/UserPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.USER.FRIENDS.PERMISSION,
      path: permissions.USER.FRIENDS.ROUTE,
      component: () => import('~/pages/friends/ui/FriendsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.PERMISSION,
      path: permissions.PROJECTS.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectsPage.vue'),
      redirect: { name: permissions.PROJECTS.ALL.PERMISSION },
      meta: { middleware: ['auth'] },
      children: [
        {
          name: permissions.PROJECTS.ALL.PERMISSION,
          path: permissions.PROJECTS.ALL.ROUTE,
          component: () => import('~/pages/projects/ui/ProjectsAllPage.vue'),
        },
        {
          name: permissions.PROJECTS.MY.PERMISSION,
          path: permissions.PROJECTS.MY.ROUTE,
          component: () => import('~/pages/projects/ui/ProjectsMyPage.vue'),
        },
        {
          name: permissions.PROJECTS.FAVORITE.PERMISSION,
          path: permissions.PROJECTS.FAVORITE.ROUTE,
          component: () => import('~/pages/projects/ui/ProjectsFavoritePage.vue'),
        },
        {
          name: permissions.PROJECTS.REQUESTS.PERMISSION,
          path: permissions.PROJECTS.REQUESTS.ROUTE,
          component: () =>
            import('~/pages/projects/ui/ProjectsRequestsPage.vue'),
        },
      ],
    },
    {
      name: permissions.PROJECTS.WORKSPACE.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectWorkspacePage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    ...createMailRoutes(),
    {
      name: permissions.PROJECTS.WORKSPACE.MEMBERS.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.MEMBERS.ROUTE,
      component: () =>
        import('~/pages/projects/ui/ProjectWorkspaceMembersPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.ROUTE,
      redirect: to => ({
        name: permissions.PROJECTS.WORKSPACE.PERMISSION,
        params: { id: to.params.id },
      }),
      meta: { middleware: ['auth'], layout: 'base' },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.SETTINGS.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.SETTINGS.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectsWorkspaceManagementSettingsPage.vue'),
      meta: { middleware: ['auth'], layout: 'base' },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MY_TEAM.PROFILE.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MY_TEAM.PROFILE.ROUTE,
      component: () => import('~/pages/projects/ui/ProfileMyTeamPage.vue'),
      meta: { middleware: ['auth'], layout: 'base' },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MY_TEAM.PROFILE.MODULE.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MY_TEAM.PROFILE.MODULE.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectsWorkspaceManagementModulePage.vue'),
      meta: { middleware: ['auth'], managementModuleFromMyTeamMember: true },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MY_TEAM.PROFILE.MODULE.DETAIL.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MY_TEAM.PROFILE.MODULE.DETAIL.ROUTE,
      component: () =>
        import('~/pages/projects/ui/ProjectsWorkspaceManagementModuleDetailPage.vue'),
      meta: { middleware: ['auth'], managementModuleFromMyTeamMember: true },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MY_TEAM.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MY_TEAM.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectsWorkspaceManagementMyTeamPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MODULE.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MODULE.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectsWorkspaceManagementModulePage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MODULE.DETAIL.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.MODULE.DETAIL.ROUTE,
      component: () =>
        import('~/pages/projects/ui/ProjectsWorkspaceManagementModuleDetailPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.MEMBERS.INVITE.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.MEMBERS.INVITE.ROUTE,
      component: () =>
        import('~/pages/projects/ui/ProjectWorkspaceMembersInvitePage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.REQUESTS.CREATE.PERMISSION,
      path: permissions.PROJECTS.REQUESTS.CREATE.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectFormPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.REQUESTS.WORKSPACE_PRESET_SELECT.PERMISSION,
      path: permissions.PROJECTS.REQUESTS.WORKSPACE_PRESET_SELECT.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectWorkspacePresetSelectPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.REQUESTS.EDIT.PERMISSION,
      path: permissions.PROJECTS.REQUESTS.EDIT.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectFormPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.REQUESTS.APPROVAL.PERMISSION,
      path: permissions.PROJECTS.REQUESTS.APPROVAL.ROUTE,
      component: () =>
        import('~/pages/projects/ui/ProjectRequestApprovePage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.PROJECT.PERMISSION,
      path: permissions.PROJECTS.PROJECT.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectPage.vue'),
      meta: { layout: 'base' },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.ABOUT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.ABOUT.ROUTE,
      redirect: to => ({
        name: permissions.PROJECTS.PROJECT.PERMISSION,
        params: { id: to.params.id },
      }),
    },
    {
      name: permissions.PROJECTS.WORKSPACE.ABOUT.EDIT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.ABOUT.EDIT.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectEditPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
      props: { isCreate: false },
    },
    {
      name: permissions.NOTIFICATIONS.PERMISSION,
      path: permissions.NOTIFICATIONS.ROUTE,
      component: () => import('~/pages/notifications/ui/NotificationsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.ADMIN.REPORTS.PERMISSION,
      path: permissions.ADMIN.REPORTS.ROUTE,
      component: () => import('~/pages/admin/reports/supporters/ui/ReportsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.ADMIN.REPORTS.DETAIL_REPORTS.PERMISSION,
      path: permissions.ADMIN.REPORTS.DETAIL_REPORTS.ROUTE,
      component: () => import('~/pages/admin/reports/supporters/ui/DetailReportsPage.vue'),
      meta: { middleware: ['auth'], layout: 'base' },
    },
    {
      name: permissions.ADMIN.REPORTS.SUPPORTERS.PERMISSION,
      path: permissions.ADMIN.REPORTS.SUPPORTERS.ROUTE,
      component: () => import('~/pages/admin/reports/supporters/ui/ProjectsReportPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.ADMIN.REPORTS.SUPPORTERS.PROJECTS.PERMISSION,
      path: permissions.ADMIN.REPORTS.SUPPORTERS.PROJECTS.ROUTE,
      component: () => import('~/pages/admin/reports/supporters/ui/SupportersReportPage.vue'),
      meta: { middleware: ['auth'] },
      props: { isGlobal: true },
    },
    {
      name: permissions.ADMIN.REPORTS.SUPPORTERS.PROJECTS.TEMPLATES.PERMISSION,
      path: permissions.ADMIN.REPORTS.SUPPORTERS.PROJECTS.TEMPLATES.ROUTE,
      component: () => import('~/pages/admin/reports/supporters/ui/TemplatesReportPage.vue'),
      meta: { layout: 'base-list', middleware: ['auth'] },
      props: { isGlobal: true },
    },
    {
      name: permissions.ADMIN.REPORTS.SUPPORTERS.PROJECTS.TEMPLATES.CREATE.PERMISSION,
      path: permissions.ADMIN.REPORTS.SUPPORTERS.PROJECTS.TEMPLATES.CREATE.ROUTE,
      component: () => import('~/pages/admin/reports/supporters/ui/FormTemplatePage.vue'),
      meta: { middleware: ['auth'] },
      props: { isGlobal: true },
    },
    {
      name: permissions.ADMIN.REPORTS.SUPPORTERS.PROJECTS.TEMPLATES.EDIT.PERMISSION,
      path: permissions.ADMIN.REPORTS.SUPPORTERS.PROJECTS.TEMPLATES.EDIT.ROUTE,
      component: () => import('~/pages/admin/reports/supporters/ui/FormTemplatePage.vue'),
      meta: { middleware: ['auth'] },
      props: { isGlobal: true },
    },
    {
      name: permissions.ADMIN.REPORTS.SUPPORTERS.PROJECTS.SEND_MESSAGE.PERMISSION,
      path: permissions.ADMIN.REPORTS.SUPPORTERS.PROJECTS.SEND_MESSAGE.ROUTE,
      component: () => import('~/pages/admin/reports/supporters/ui/SendMessagePage.vue'),
      meta: { middleware: ['auth'] },
      props: { isGlobal: true },
    },
    {
      name: permissions.ADMIN.PERMISSIONS.PERMISSION,
      path: permissions.ADMIN.PERMISSIONS.ROUTE,
      component: () => import('~/pages/admin/permissions/ui/PermissionsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.NOT_FOUND.PERMISSION,
      path: permissions.NOT_FOUND.ROUTE,
      component: () => import('~/pages/404/ui/404Page.vue'),
    },
    {
      name: permissions.FORBIDDEN.PERMISSION,
      path: permissions.FORBIDDEN.ROUTE,
      component: () => import('~/pages/forbidden/ui/ForbiddenPage.vue'),
    },

    // event modules (passport / program / regulation)
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_PASSPORT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_PASSPORT.ROUTE,
      component: () => import('~/pages/round-table/passport/EventPassportEntryPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_PASSPORT.EDIT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_PASSPORT.EDIT.ROUTE,
      component: () => import('~/pages/round-table/passport/EventPassportEditPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_PASSPORT.VIEW.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_PASSPORT.VIEW.ROUTE,
      component: () => import('~/pages/round-table/passport/EventPassportViewPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_PASSPORT.PARTICIPANTS.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_PASSPORT.PARTICIPANTS.ROUTE,
      component: () => import('~/pages/round-table/passport/EventPassportParticipantsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_PROGRAM.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_PROGRAM.ROUTE,
      component: () => import('~/pages/round-table/program/EventProgramEntryPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_PROGRAM.EDIT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_PROGRAM.EDIT.ROUTE,
      component: () => import('~/pages/round-table/program/EventProgramEditPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_PROGRAM.VIEW.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_PROGRAM.VIEW.ROUTE,
      component: () => import('~/pages/round-table/program/EventProgramViewPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_REGULATION.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_REGULATION.ROUTE,
      component: () => import('~/pages/round-table/regulation/EventRegulationEntryPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_REGULATION.EDIT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_REGULATION.EDIT.ROUTE,
      component: () => import('~/pages/round-table/regulation/EventRegulationEditPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_REGULATION.VIEW.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_REGULATION.VIEW.ROUTE,
      component: () => import('~/pages/round-table/regulation/EventRegulationViewPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_APPLICATION.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_APPLICATION.ROUTE,
      component: () => import('~/pages/round-table/application/EventApplicationPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EVENT_APPLICATION.APPROVAL.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EVENT_APPLICATION.APPROVAL.ROUTE,
      component: () => import('~/pages/round-table/application/EventApplicationApprovalPage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
    },

    // emergency contacts
    {
      name: permissions.PROJECTS.WORKSPACE.EMERGENCY_CONTACTS.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EMERGENCY_CONTACTS.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectWorkspaceEmergencyContactsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.EMERGENCY_CONTACTS.CATEGORY.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.EMERGENCY_CONTACTS.CATEGORY.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectWorkspaceEmergencyContactsCategoryPage.vue'),
      meta: { middleware: ['auth'] },
    },

    // supporters
    ...createSupportersRoutes(SUPPORTERS_SUBMODULE_CONFIG.WORKSPACE),

    /**
     * Surveys
     */
    ...createSurveyRoutes(SURVEYS_SUBMODULE_CONFIG.WORKSPACE),
    ...createSurveyRoutes(SURVEYS_SUBMODULE_CONFIG.QUESTION_TO_DEPUTY),
    {
      name: permissions.SHARED.SURVEYS.FILL.PERMISSION,
      path: permissions.SHARED.SURVEYS.FILL.ROUTE,
      component: () => import('~/pages/surveys/ui/SurveysFillPage.vue'),
      props: {
        isShared: true,
        access: {
          showCreateSurveyBtn: [permissions.SHARED.SURVEYS.FILL.SHOW_CREATE_SURVEY_BTN.PERMISSION],
          showShareLinkBtn: [permissions.SHARED.SURVEYS.FILL.SHOW_SHARE_LINK_BTN.PERMISSION],
          showArchiveTab: [permissions.SHARED.SURVEYS.FILL.SHOW_ARCHIVE_TAB.PERMISSION],
          showStatisticsTab: [permissions.SHARED.SURVEYS.FILL.SHOW_STATISTICS_TAB.PERMISSION],
        },
      },
    },

    /**
     * News
     */
    ...createNewsRoutes(NEWS_SUBMODULE_CONFIG.GLOBAL),
    ...createNewsRoutes(NEWS_SUBMODULE_CONFIG.GENERAL_ENTERTAINMENT),
    ...createNewsRoutes(NEWS_SUBMODULE_CONFIG.WORKSPACE),
    ...createNewsRoutes(NEWS_SUBMODULE_CONFIG.WORKSPACE_RIGHTS_OF_CONSUMER),
    ...createNewsRoutes(NEWS_SUBMODULE_CONFIG.WORKSPACE_WHAT_TO_DO_IN_CASE_OF_ACCIDENT),
    ...createNewsRoutes(NEWS_SUBMODULE_CONFIG.WORKSPACE_MY_SPEECH),

    /**
     * Documents
     */
    {
      name: permissions.DOCUMENTS.PERMISSION,
      path: permissions.DOCUMENTS.ROUTE,
      component: () => import('~/pages/documents/ui/DocumentsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.DOCUMENTS.CREATE.PERMISSION,
      path: permissions.DOCUMENTS.CREATE.ROUTE,
      component: () => import('~/pages/documents/ui/DocumentsFormPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.SHOP.PERMISSION,
      path: permissions.SHOP.ROUTE,
      component: () => import('~/pages/shop/ui/ShopPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.SHOP.POINTS.PERMISSION,
      path: permissions.SHOP.POINTS.ROUTE,
      component: () => import('~/pages/shop/ui/ShopPointsPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.SHOP.DETAIL.PERMISSION,
      path: permissions.SHOP.DETAIL.ROUTE,
      component: () => import('~/pages/shop/ui/ShopAdvertisingDetailPage.vue'),
      meta: { middleware: ['auth'] },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectTemplatePage.vue'),
      meta: { layout: 'base', middleware: ['auth'] },
      redirect: { name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.NO_TEMPLATE.PERMISSION },
      children: [
        {
          name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.NO_TEMPLATE.PERMISSION,
          path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.NO_TEMPLATE.ROUTE,
          component: () => import('~/entities/projects/ui/templates/WithoutTemplate.vue'),
        },
        {
          name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.ONE_TEMPLATE.PERMISSION,
          path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.ONE_TEMPLATE.ROUTE,
          component: () => import('~/entities/projects/ui/templates/OneTemplate.vue'),
        },
        {
          name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.TWO_TEMPLATE.PERMISSION,
          path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.TWO_TEMPLATE.ROUTE,
          component: () => import('~/entities/projects/ui/templates/TwoTemplate.vue'),
        },
        {
          name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.THREE_TEMPLATE.PERMISSION,
          path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.THREE_TEMPLATE.ROUTE,
          component: () => import('~/entities/projects/ui/templates/ThreeTemplate.vue'),
        },
        {
          name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.FOUR_TEMPLATE.PERMISSION,
          path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.FOUR_TEMPLATE.ROUTE,
          component: () => import('~/entities/projects/ui/templates/FourTemplate.vue'),
        },
        {
          name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.FIVE_TEMPLATE.PERMISSION,
          path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.FIVE_TEMPLATE.ROUTE,
          component: () => import('~/entities/projects/ui/templates/FiveTemplate.vue'),
        },
      ],
    },
    {
      name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.NO_TEMPLATE.EDIT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.NO_TEMPLATE.EDIT.ROUTE,
      component: () => import('~/entities/projects/ui/templates/WithoutTemplateEdit.vue'),
      meta: { middleware: ['auth'], layout: 'base' },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.ONE_TEMPLATE.EDIT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.ONE_TEMPLATE.EDIT.ROUTE,
      component: () => import('~/entities/projects/ui/templates/OneTemplateEdit.vue'),
      meta: { middleware: ['auth'], layout: 'base' },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.TWO_TEMPLATE.EDIT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.TWO_TEMPLATE.EDIT.ROUTE,
      component: () => import('~/entities/projects/ui/templates/TwoTemplateEdit.vue'),
      meta: { middleware: ['auth'], layout: 'base' },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.THREE_TEMPLATE.EDIT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.THREE_TEMPLATE.EDIT.ROUTE,
      component: () => import('~/entities/projects/ui/templates/ThreeTemplateEdit.vue'),
      meta: { middleware: ['auth'], layout: 'base' },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.FOUR_TEMPLATE.EDIT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.FOUR_TEMPLATE.EDIT.ROUTE,
      component: () => import('~/entities/projects/ui/templates/FourTemplateEdit.vue'),
      meta: { middleware: ['auth'], layout: 'base' },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.FIVE_TEMPLATE.EDIT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.FIVE_TEMPLATE.EDIT.ROUTE,
      component: () => import('~/entities/projects/ui/templates/FiveTemplateEdit.vue'),
      meta: { middleware: ['auth'], layout: 'base' },
    },
    {
      name: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.MODULE_SELECT.PERMISSION,
      path: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.MODULE_SELECT.ROUTE,
      component: () => import('~/pages/projects/ui/ProjectLandingModuleSelectPage.vue'),
      meta: { middleware: ['auth'], layout: 'base' },
    },
    {
      name: permissions.LEGAL.PRIVACY.PERMISSION,
      path: permissions.LEGAL.PRIVACY.ROUTE,
      component: () => import('~/pages/legal/privacy/ui/PrivacyPage.vue'),
      meta: { layout: 'base' },
    },
    {
      name: permissions.LEGAL.CONSENT.PERMISSION,
      path: permissions.LEGAL.CONSENT.ROUTE,
      component: () => import('~/pages/legal/consent/ui/ConsentPage.vue'),
      meta: { layout: 'base' },
    },
    {
      name: permissions.LEGAL.ADVERTISING.PERMISSION,
      path: permissions.LEGAL.ADVERTISING.ROUTE,
      component: () => import('~/pages/legal/advertising/ui/AdvertisingPage.vue'),
      meta: { layout: 'base' },
    },
    {
      name: permissions.LEGAL.COOKIES.PERMISSION,
      path: permissions.LEGAL.COOKIES.ROUTE,
      component: () => import('~/pages/legal/cookies/ui/CookiesPage.vue'),
      meta: { layout: 'base' },
    },
    {
      name: permissions.DOCS.REESTR_PO.PERMISSION,
      path: permissions.DOCS.REESTR_PO.ROUTE,
      component: () => import('~/pages/docs/reestr-po/ui/ReestrDocsPage.vue'),
      meta: { layout: 'base' },
    },
    {
      name: permissions.DOCS.OPEN_SOURCE.PERMISSION,
      path: permissions.DOCS.OPEN_SOURCE.ROUTE,
      component: () => import('~/pages/docs/open-source/ui/OpenSourcePage.vue'),
      meta: { layout: 'base' },
    },
  ],
}
